import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Loading from "../../components/Loading";
import Meta from "../../components/Meta";
import ReCaptcha from "../../components/ReCaptcha";

import Data from '../../../repository';
import { forgottenPasswordSchema } from '../../../validation/schemas';

import styles from "../../../styles/Entry.module.scss";

function ForgottenPassword() {
	const [loading, setLoading] = useState(false);
	const [serverError, setServerError] = useState(null);
	const [success, setSuccess] = useState(false);
	const [recaptchaToken, setRecaptchaToken] = useState(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm({
		resolver: yupResolver(forgottenPasswordSchema),
		defaultValues: {
			email: ''
		}
	});

	const onRecaptchaChange = (token) => {
		setRecaptchaToken(token);
		if (token) {
			setServerError(null);
		}
	}

	const onSubmit = async (data) => {
		// Validate reCAPTCHA
		if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true' && !recaptchaToken) {
			setServerError("Please complete the reCAPTCHA verification.");
			return;
		}

		setLoading(true);
		setServerError(null);

		try {
			await Data.requestPasswordReset(data.email, recaptchaToken);
			reset();
			setRecaptchaToken(null);
			setSuccess(true);
		} catch (error) {
			reset();
			setRecaptchaToken(null);
			setServerError(error.response?.data?.message || 'An error occurred. Please try again.');
		} finally {
			setLoading(false);
		}
	}

	if (!success) {
		return (
			<>
				<Meta title="Forgotten Password?" />

				<div>
					{serverError &&
						<div className={styles.error}>
							<p>{serverError}</p>
						</div>
					}

					<h1>Forgotten Password</h1>

					<form onSubmit={handleSubmit(onSubmit)}>
						<div>
							<input
								type="email"
								placeholder="Email Address"
								{...register('email')}
								maxLength={255}
								className={errors.email ? 'input-error' : ''}
							/>
							{errors.email && <p className={styles.fieldError}>{errors.email.message}</p>}
						</div>
						<ReCaptcha
							onChange={onRecaptchaChange}
							onExpired={() => onRecaptchaChange(null)}
						/>
						<button className="btn btnPrimary" disabled={loading}>
							{loading ?
								<Loading button={true} />
								:
								`Request Password Reset`
							}
						</button>
					</form>

					<div className={styles.alternatives}>
						<Link to="/entry/login">Remember your password?</Link>
					</div>
				</div>
			</>
		);
	} else {
		return (
			<div>
				<h1>Forgotten Password</h1>
				<p>We've sent a password reset link to your email address. If you don't receive the email, check your spam folder.</p>

				<div className={styles.alternatives}>
					<Link to="/entry/login">Back to Login</Link>
				</div>
			</div>
		);
	}
}

export default ForgottenPassword;
